import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import PropTypes from 'prop-types';
import ResourceBadge from './ResourceBadge';

// Primary blue used for the "selected" (first / co-first author) star marker
const STAR_COLOR = '#2F58CD';

// Star with slightly rounded tips (via round line joins) for the "selected" marker
const StarIcon = ({ size, style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={STAR_COLOR}
    stroke={STAR_COLOR}
    strokeWidth="2.2"
    strokeLinejoin="round"
    strokeLinecap="round"
    style={style}
    aria-hidden="true"
  >
    <path d="M12 2.6 L14.99 8.66 L21.68 9.63 L16.84 14.35 L17.98 21.02 L12 17.87 L6.02 21.02 L7.16 14.35 L2.32 9.63 L9.01 8.66 Z" />
  </svg>
);

StarIcon.propTypes = {
  size: PropTypes.number.isRequired,
  style: PropTypes.object,
};

const tagStyles = {
  'LLM Reasoning': {
    backgroundColor: '#EEF2FF',
    color: '#4338CA',
    borderColor: '#C7D2FE',
  },
  'Reinforcement Learning': {
    backgroundColor: '#FFF7ED',
    color: '#C2410C',
    borderColor: '#FED7AA',
  },
  'Task (Env) Generation': {
    backgroundColor: '#F1FCF4',
    color: '#196D39',
    borderColor: '#ADE6C1',
  },
};

const TagChip = ({ label }) => {
  const style = tagStyles[label] || {};
  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{
        fontSize: '0.72rem',
        fontWeight: 600,
        height: '22px',
        borderRadius: '6px',
        letterSpacing: '0.01em',
        ...style,
      }}
    />
  );
};

TagChip.propTypes = {
  label: PropTypes.string.isRequired,
};

// Marks where the (newest-first) list crosses into a new year.
const YearHeading = ({ year, isFirst }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, pt: isFirst ? 0 : 3, mb: 3.5 }}>
    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#9AA0AE', letterSpacing: '0.06em' }}>
      {year}
    </Typography>
    <Box sx={{ flexGrow: 1, height: '1px', backgroundColor: '#E8EBF2' }} />
  </Box>
);

YearHeading.propTypes = {
  year: PropTypes.number.isRequired,
  isFirst: PropTypes.bool.isRequired,
};

const PublicationItem = ({ isMobile, selected, title, authors, conference, affiliation, note, additionalInfo, paperLink, paperLabel, leaderboardLink, datasetLink, modelLink, codeLink, projectLink, projectLabel, posterLink, xLink, tags }) => (
  <Box sx={{ mb:(!isMobile && 4) || (isMobile && 5) }}>
    {(selected || (tags && tags.length > 0)) && (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5, alignItems: 'center' }}>
        {selected && (
          <StarIcon size={18} style={{ marginRight: '3px', flexShrink: 0 }} />
        )}
        {tags && tags.map((tag) => (
          <TagChip key={tag} label={tag} />
        ))}
      </Box>
    )}
    <Box sx={{
      flexDirection: !isMobile && 'row',
      display: !isMobile && 'flex',
      alignItems: 'center',
    }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {title}
      <Box sx={{
        '& .MuiTypography-root': { fontSize: '0.9375rem', color: '#333333' },
        '& a': { color: 'inherit', '&:hover': { color: '#0b3798' } },
      }}>
        {authors}
      </Box>
      {(conference || additionalInfo) && <Typography component="div" sx={{
        fontSize: '0.9375rem',
        color: '#333333',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        rowGap: 0.5,
        '& .MuiTypography-root': { fontSize: 'inherit' },
      }}>
        {conference ? <><b>{conference}</b> {additionalInfo}</> : additionalInfo}
      </Typography>}
      {affiliation && <Typography sx={{ fontSize: '0.9rem', color: '#666666'}}>{affiliation}</Typography>}
      {note}

      {(paperLink || leaderboardLink || datasetLink || modelLink || codeLink || projectLink || posterLink || xLink) && <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0 }}>
        {[
          paperLink && { label: paperLabel || 'PDF', link: paperLink },
          leaderboardLink && { label: 'Leaderboard', link: leaderboardLink },
          datasetLink && { label: 'Dataset', link: datasetLink },
          modelLink && { label: 'Model', link: modelLink },
          codeLink && { label: 'Code', link: codeLink },
          projectLink && { label: projectLabel || 'Project Page', link: projectLink },
          posterLink && { label: 'Poster', link: posterLink },
          xLink && { label: 'Thread', link: xLink },
        ].filter(Boolean).map((item, idx, arr) => (
          <span key={item.label} style={{ display: 'inline-flex', alignItems: 'center', minHeight: 24 }}>
            <Typography
              component="a"
              data-resource-link
              href={item.link}
              target="_blank"
              rel="noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: '20px',
                lineHeight: 1,
                fontSize: '0.88rem',
                color: '#2F58CD',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {item.label}
            </Typography>
            <ResourceBadge url={item.link} />
            {idx < arr.length - 1 && (
              <Typography sx={{ display: 'inline-flex', alignItems: 'center', height: '20px', lineHeight: 1, mx: 0.8, color: '#bbb', fontSize: '0.88rem', userSelect: 'none' }}>|</Typography>
            )}
          </span>
        ))}
      </Box>}
      </Box>
    </Box>
  </Box>
);

PublicationItem.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  selected: PropTypes.bool,
  title: PropTypes.string.isRequired,
  authors: PropTypes.string.isRequired,
  affiliation: PropTypes.string.isRequired,
  note: PropTypes.node,
  conference: PropTypes.string.isRequired,
  additionalInfo: PropTypes.string.isRequired,
  paperLink: PropTypes.string,
  paperLabel: PropTypes.string,
  leaderboardLink: PropTypes.string,
  datasetLink: PropTypes.string,
  modelLink: PropTypes.string,
  codeLink: PropTypes.string,
  projectLink: PropTypes.string,
  projectLabel: PropTypes.string,
  posterLink: PropTypes.string,
  xLink: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.string),
};

export default function Publications({ isMobile }) {
  const publications = [
    {
      selected: true,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [W4] ProgramDistill: From Interactive Web Apps to Verifiable Reference-Guided SWE Tasks
      </Typography>,
      authors:
      <Typography>
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>,{" "}
        <a href="https://kim-minseon.github.io/" target='_blank' rel="noreferrer" className="no-underline">Minseon Kim</a>,{" "}
        <a href="https://www.microsoft.com/en-us/research/people/youki/" target='_blank' rel="noreferrer" className="no-underline">Young Jin Kim</a>,{" "}
        <a href="https://www.microsoft.com/en-us/research/people/matpereira/" target='_blank' rel="noreferrer" className="no-underline">Matheus Pereira</a>,{" "}
        <a href="https://www.microsoft.com/en-us/research/people/macote/?lang=ko-kr" target='_blank' rel="noreferrer" className="no-underline">Marc-Alexandre Côté</a>,{" "}
        <a href="https://mila.quebec/en/directory/alessandro-sordoni" target='_blank' rel="noreferrer" className="no-underline">Alessandro Sordoni</a>,{" "}
        <a href="https://xingdi-eric-yuan.github.io/" target='_blank' rel="noreferrer" className="no-underline">Xingdi Yuan</a>,{" "}
        <a href="https://shizhengyan.github.io/" target='_blank' rel="noreferrer" className="no-underline">Zhengyan Shi</a>
      </Typography>,
      affiliation: 'Work done during the Microsoft Research Internship',
      conference: 'NeurIPS 2026 Workshop on Interpreting Agent Behavior',
      paperLink: 'https://microsoft.github.io/debug-gym/static/papers/ProgramDistill_arxiv.pdf',
      paperLabel: 'Paper',
      leaderboardLink: 'https://microsoft.github.io/debug-gym/blog/2026/09/programdistill/dashboard/',
      datasetLink: 'https://huggingface.co/datasets/microsoft/ProgramDistill',
      projectLink: 'https://microsoft.github.io/debug-gym/blog/2026/09/programdistill/',
      projectLabel: 'Blog',
      xLink: 'https://x.com/beanie0__0/status/2100282654428176491',
      tags: ['Task (Env) Generation', 'LLM Reasoning'],
      year: 2026,
    },
    {
      selected: false,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [P3] FrogNano: Training a 4B Coding Agent via Online Task Synthesis
      </Typography>,
      authors:
      <Typography>
        <a href="https://kim-minseon.github.io/" target='_blank' rel="noreferrer" className="no-underline">Minseon Kim</a>*,{" "}
        <a href="https://shizhengyan.github.io/" target='_blank' rel="noreferrer" className="no-underline">Zhengyan Shi</a>*,{" "}
        <a href="https://emilianopp.com/#/home" target='_blank' rel="noreferrer" className="no-underline">Emiliano Penaloza</a>*,{" "}
        <a href="https://christopherzc.github.io/" target='_blank' rel="noreferrer" className="no-underline">Christopher Cui</a>*,{" "}
        <a href="https://roger-creus.github.io/" target='_blank' rel="noreferrer" className="no-underline">Roger Creus Castanyer</a>*,{" "}
        <a href="https://mhashemzadeh.github.io/home/" target='_blank' rel="noreferrer" className="no-underline">Maryam Hashemzadeh</a>,{" "}
        <a href="https://icwhite.github.io/website/" target='_blank' rel="noreferrer" className="no-underline">Isadora White</a>,{" "}
        <a href="https://jonathanmli.github.io/" target='_blank' rel="noreferrer" className="no-underline">Jonathan Light</a>,{" "}
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>,{" "}
        <a href="https://www.microsoft.com/en-us/research/people/matpereira/" target='_blank' rel="noreferrer" className="no-underline">Matheus Pereira</a>,{" "}
        <a href="https://www.microsoft.com/en-us/research/people/dmoldavskaya/" target='_blank' rel="noreferrer" className="no-underline">Darya Moldavskaya</a>,{" "}
        <a href="https://www.microsoft.com/en-us/research/people/chsingh/?lang=ko-kr" target='_blank' rel="noreferrer" className="no-underline">Chinmay Singh</a>,{" "}
        <a href="https://www.microsoft.com/en-us/research/people/fabiovera/" target='_blank' rel="noreferrer" className="no-underline">Fabio Vera</a>,{" "}
        <a href="https://www.microsoft.com/en-us/research/people/baolinpeng/" target='_blank' rel="noreferrer" className="no-underline">Baolin Peng</a>,{" "}
        <a href="https://xingdi-eric-yuan.github.io/" target='_blank' rel="noreferrer" className="no-underline">Xingdi Yuan</a>†,{" "}
        <a href="https://www.microsoft.com/en-us/research/people/macote/?lang=ko-kr" target='_blank' rel="noreferrer" className="no-underline">Marc-Alexandre Côté</a>†,{" "}
        <a href="https://mila.quebec/en/directory/alessandro-sordoni" target='_blank' rel="noreferrer" className="no-underline">Alessandro Sordoni</a>†
      </Typography>,
      affiliation: 'Work done during the Microsoft Research Internship',
      tags: ['Task (Env) Generation', 'Reinforcement Learning'],
      year: 2026,
      paperLink: "https://arxiv.org/pdf/2609.07925",
      modelLink: "https://huggingface.co/microsoft/FrogNano-4B-2609",
      xLink: "https://x.com/kim__minseon/status/2097405882464145676",
    },
    {
      selected: false,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [P5] Shockingly Simple Self-retrospection Improves Agentic Models Without RL
      </Typography>,
      authors:
      <Typography>
        <a href="https://jonathanmli.github.io/" target='_blank' rel="noreferrer" className="no-underline">Jonathan Light</a>,{" "}
        <a href="https://christopherzc.github.io/" target='_blank' rel="noreferrer" className="no-underline">Christopher Zhang Cui</a>,{" "}
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>,{" "}
        <a href="https://roger-creus.github.io/" target='_blank' rel="noreferrer" className="no-underline">Roger Creus Castanyer</a>,{" "}
        <a href="https://emilianopp.com/#/home" target='_blank' rel="noreferrer" className="no-underline">Emiliano Penaloza</a>,{" "}
        <a href="https://shizhengyan.github.io/" target='_blank' rel="noreferrer" className="no-underline">Zhengyan Shi</a>,{" "}
        <a href="https://mila.quebec/en/directory/alessandro-sordoni" target='_blank' rel="noreferrer" className="no-underline">Alessandro Sordoni</a>,{" "}
        <a href="https://www.microsoft.com/en-us/research/people/macote/?lang=ko-kr" target='_blank' rel="noreferrer" className="no-underline">Marc-Alexandre Côté</a>,{" "}
        <a href="https://xingdi-eric-yuan.github.io/" target='_blank' rel="noreferrer" className="no-underline">Xingdi Yuan</a>,{" "}
        <a href="https://kim-minseon.github.io/" target='_blank' rel="noreferrer" className="no-underline">Minseon Kim</a>
      </Typography>,
      affiliation: 'Work done during the Microsoft Research Internship',
      tags: ['LLM Reasoning'],
      year: 2026,
      paperLink: 'https://arxiv.org/pdf/2609.35741',
    },
    {
      selected: false,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [P4] TISD: On-Policy Self-Distillation with Trajectory Intervention
      </Typography>,
      authors:
      <Typography>
        Taeckyung Lee, Rinat Amankos,{" "}
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>,{" "}
        Hyungjun Yoon, Woogyeol Jin, Sung-Ju Lee
      </Typography>,
      tags: ['LLM Reasoning'],
      year: 2026,
      paperLink: 'https://arxiv.org/pdf/2609.30878',
      codeLink: 'https://github.com/taeckyung/TISD',
      projectLink: 'https://nmsl.kaist.ac.kr/projects/tisd/',
    },
    {
      selected: true,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [C14] Rebellious Student: Reversing Teacher Signals for Reasoning Exploration with Self-Distilled RLVR
      </Typography>,
      authors:
      <Typography>
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>*,{" "}<a href="https://jiwonjeon9603.github.io/" target='_blank' rel="noreferrer" className="no-underline">Jiwon Jeon</a>*, <a href="https://www.microsoft.com/en-us/research/people/dongsli/" target='_blank' rel="noreferrer" className="no-underline">Dongsheng Li</a>,{" "}<a href="https://www.microsoft.com/en-us/research/people/yuqyang/" target='_blank' rel="noreferrer" className="no-underline">Yuqing Yang</a>
      </Typography>,
      affiliation: 'Work done during the Microsoft Research Internship',
      tags: ['LLM Reasoning', 'Reinforcement Learning'],
      note: <Typography sx={{ fontSize: '0.9rem', color: '#666666' }}>Community summary: <a href="https://x.com/a_weers/status/2068446249746153821" target="_blank" rel="noreferrer">@a_weers</a></Typography>,
      conference: 'NeurIPS 2026',
      additionalInfo: <>
        <Typography sx={{ fontSize: '1rem', width: '100%' }}>
          COLM 2026 Workshop on Efficient Reasoning
          <Typography component="span" sx={{ fontSize: '1rem', color: 'red', marginLeft: '8px', whiteSpace: 'nowrap' }}><a href="https://drive.google.com/file/d/13oYYACk8WUmBO7MlT6kuWwk97dhV_k0R/view?usp=sharing" target="_blank" rel="noopener noreferrer"><b>Best Paper Award Nominee</b></a></Typography>
        </Typography>
        <Typography sx={{ fontSize: '1rem', width: '100%' }}>
          NeurIPS 2026 Workshop on Transitioning from Pre-Training to Post-Training
        </Typography>
      </>,
      year: 2026,
      paperLink: "https://arxiv.org/pdf/2605.10781",
      posterLink: "https://docs.google.com/presentation/d/1OftzImmi9TcS1UtOcpxbkW-aU8U_xbMi8TJY0MtqCJU/edit?usp=sharing",
      codeLink: "https://github.com/beanie00/self-distillation-analysis",
      projectLink: "https://beanie00.github.io/self-distillation-analysis/rlrt/",
      xLink: "https://x.com/beanie0__0/status/2054085166621184255",
    },
    {
      selected: false,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [C13] Are We Measuring Strategy or Phrasing? The Gap Between Surface- and Approach-Level Diversity in LLM Math Reasoning
      </Typography>,
      authors:
      <Typography>
        Sangmook Lee,{" "}
        <a href="https://minbeomkim.github.io/" target='_blank' rel="noreferrer" className="no-underline">Minbeom Kim</a>,{" "}
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>,{" "}
        <a href="https://dohyungk1m.github.io/" target='_blank' rel="noreferrer" className="no-underline">Dohyung Kim</a>,{" "}
        Sojeong Rhee,{" "}
        <a href="http://milab.snu.ac.kr/kjung/index.html" target='_blank' rel="noreferrer" className="no-underline">Kyomin Jung</a>
      </Typography>,
      conference: 'EMNLP 2026',
      additionalInfo: <>
        <Typography sx={{ fontSize: '1rem', width: '100%' }}>
          ICML Workshop on AI for Math 2026
          <Typography component="span" sx={{ fontSize: '1rem', color: 'red', marginLeft: '8px', whiteSpace: 'nowrap' }}><b>Spotlight (Top 3%)</b></Typography>
        </Typography>
      </>,
      year: 2026,
      paperLink: "https://arxiv.org/pdf/2606.29985",
      tags: ['LLM Reasoning'],
    },
    {
      selected: false,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [W3] Inject a Turn, Not a Token: Learning Where to Redirect LLM Reasoning for Self-Correction
      </Typography>,
      authors:
      <Typography>
        <a href="https://jiwonjeon9603.github.io/" target='_blank' rel="noreferrer" className="no-underline">Jiwon Jeon</a>,{" "}
        Myungsik Cho,{" "}
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>,{" "}
        <a href="https://sites.google.com/view/youngchulsung" target='_blank' rel="noreferrer" className="no-underline">Youngchul Sung</a>
      </Typography>,
      conference: 'COLM 2026 Workshop on Efficient Reasoning',
      additionalInfo: <Typography component="span" sx={{ fontSize: '1rem', color: 'red', marginLeft: '8px', whiteSpace: 'nowrap' }}><b>Spotlight</b></Typography>,
      year: 2026,
      paperLink: "https://openreview.net/pdf?id=g8QeduCUJ6",
      tags: ['LLM Reasoning'],
    },
    {
      selected: false,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [P2] Be My Tutor: On-Policy Co-Distillation for Mutual LLM Improvement via Peer Feedback
      </Typography>,
      authors:
      <Typography>
        Woohyeon Byeon, <a href="https://jiwonjeon9603.github.io/" target='_blank' rel="noreferrer" className="no-underline">Jiwon Jeon</a>,{" "}<strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>, <a href="https://sites.google.com/view/youngchulsung" target='_blank' rel="noreferrer" className="no-underline">Youngchul Sung</a>
      </Typography>,
      tags: ['LLM Reasoning'],
      year: 2026,
      paperLink: "https://arxiv.org/pdf/2606.14368",
    },
    {
      selected: true,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [C12] Why Does Self-Distillation (Sometimes) Degrade the Reasoning Capability of LLMs?
      </Typography>,
      authors:
      // <Typography>
      //   Jeonghye Kim, Xufang Luo, Minbeom Kim, Sangmook Lee, Dohyung Kim, Jiwon Jeon, Dongsheng Li, Yuqing Yang
      // </Typography>,
      <Typography>
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>, <a href="https://www.alphaxiv.org/@xufang-luo" target='_blank' rel="noreferrer" className="no-underline">Xufang Luo</a>,{" "}<a href="https://minbeomkim.github.io/" target='_blank' rel="noreferrer" className="no-underline">Minbeom Kim</a>,{" "}Sangmook Lee, <a href="https://dohyungk1m.github.io/" target='_blank' rel="noreferrer" className="no-underline">Dohyung Kim</a>, <a href="https://jiwonjeon9603.github.io/" target='_blank' rel="noreferrer" className="no-underline">Jiwon Jeon</a>, <a href="https://www.microsoft.com/en-us/research/people/dongsli/" target='_blank' rel="noreferrer" className="no-underline">Dongsheng Li</a>,{" "}<a href="https://www.microsoft.com/en-us/research/people/yuqyang/" target='_blank' rel="noreferrer" className="no-underline">Yuqing Yang</a>
      </Typography>,
      affiliation: 'Work done during the Microsoft Research Internship',
      tags: ['LLM Reasoning'],
      conference: 'COLM 2026',
      additionalInfo: <>
        <Typography sx={{ fontSize: '1rem', width: '100%' }}>
          COLM Workshop on Context Beyond the Window 2026
          <Typography component="span" sx={{ fontSize: '1rem', color: 'red', marginLeft: '8px', whiteSpace: 'nowrap' }}><b>Travel Award</b></Typography>
        </Typography>
        <Typography component="a" href="https://huggingface.co/papers/2603.24472" target="_blank" rel="noreferrer" sx={{ fontSize: '1rem', width: '100%', color: '#FF9D00', textDecoration: 'none', '&:hover': { color: '#FF9D00', textDecoration: 'underline' } }}>🤗 #2 Paper of the Day</Typography>
      </>,
      year: 2026,
      paperLink: "https://arxiv.org/pdf/2603.24472",
      posterLink: "https://docs.google.com/presentation/d/19dvsi2i8BBRdY4fN3sj4diIW7Ph_xXE0NnDmrQ_oG0k/edit?usp=sharing",
      xLink: "https://x.com/beanie0__0/status/2037018293735952471",
      codeLink: "https://github.com/beanie00/self-distillation-analysis",
      projectLink: "https://beanie00.notion.site/why-does-self-distillation-degrade-reasoning?source=copy_link",
      projectLabel: "Blog",
    },
    {
      selected: false,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [W2] Dense Supervision but Sparse Guidance: A Token-Level Analysis of On-Policy Self-Distillation
      </Typography>,
      authors:
      <Typography>
        Taeckyung Lee,{" "}
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>,{" "}
        Hyungjun Yoon,{" "}
        Sung-Ju Lee
      </Typography>,
      conference: 'ICML Workshop on AI for Math 2026',
      year: 2026,
      paperLink: "https://openreview.net/pdf?id=IfAQFQ56Zi",
      tags: ['LLM Reasoning'],
    },
    {
      selected: false,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [C11] Beyond Normalization: Rethinking the Partition Function as a Difficulty Scheduler for RLVR
      </Typography>,
      authors:
      <Typography>
        <a href="https://dohyungk1m.github.io/" target='_blank' rel="noreferrer" className="no-underline">Dohyung Kim</a>, <a href="https://minbeomkim.github.io/" target='_blank' rel="noreferrer" className="no-underline">Minbeom Kim</a>,{" "}<strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>, Sangmook Lee, Sojeong Rhee, <a href="http://milab.snu.ac.kr/kjung/index.html" target='_blank' rel="noreferrer" className="no-underline">Kyomin Jung</a>
      </Typography>,
      tags: ['LLM Reasoning', 'Reinforcement Learning'],
      conference: 'ICML 2026',
      year: 2026,
      paperLink: "https://arxiv.org/pdf/2602.12642"
    },
    {
      selected: true,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [P1] Understanding Reasoning in LLMs through Strategic Information Allocation under Uncertainty
      </Typography>,
      authors:
      <Typography>
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>, <a href="https://www.alphaxiv.org/@xufang-luo" target='_blank' rel="noreferrer" className="no-underline">Xufang Luo</a>,{" "}<a href="https://minbeomkim.github.io/" target='_blank' rel="noreferrer" className="no-underline">Minbeom Kim</a>,{" "}Sangmook Lee, <a href="https://www.microsoft.com/en-us/research/people/dongsli/" target='_blank' rel="noreferrer" className="no-underline">Dongsheng Li</a>,{" "}<a href="https://www.microsoft.com/en-us/research/people/yuqyang/" target='_blank' rel="noreferrer" className="no-underline">Yuqing Yang</a>
      </Typography>,
      affiliation: 'Work done during the Microsoft Research Internship',
      tags: ['LLM Reasoning'],
      year: 2026,
      paperLink: "https://arxiv.org/pdf/2603.15500",
      codeLink: "https://github.com/beanie00/strategic-information-allocation-llm-reasoning",
    },
    {
      selected: false,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [C10] Align While Search: Belief-Guided Exploratory Inference for World-Grounded Embodied Agents
      </Typography>,
      authors:
      <Typography>
        <a href="https://seohuibae.github.io/" target='_blank' rel="noreferrer" className="no-underline">Seohui Bae</a>,{" "}
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>,{" "}
        <a href="https://sites.google.com/view/youngchulsung" target='_blank' rel="noreferrer" className="no-underline">Youngchul Sung</a>,{" "}
        <a href="https://sites.google.com/view/woohyunglim" target='_blank' rel="noreferrer" className="no-underline">Woohyung Lim</a>
      </Typography>,
      year: 2026,
      paperLink: "https://arxiv.org/pdf/2512.24461",
      conference: 'CVPR 2026',
      tags: ['LLM Reasoning'],
    },
    {
      selected: true,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [C9] Exploratory Memory-Augmented LLM Agent via Hybrid On- and Off-Policy Optimization
      </Typography>,
      authors:
      <Typography>
        Zeyuan Liu*, <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>*, <a href="https://www.alphaxiv.org/@xufang-luo" target='_blank' rel="noreferrer" className="no-underline">Xufang Luo</a>,{" "}<a href="https://www.microsoft.com/en-us/research/people/dongsli/" target='_blank' rel="noreferrer" className="no-underline">Dongsheng Li</a>,{" "}<a href="https://www.microsoft.com/en-us/research/people/yuqyang/" target='_blank' rel="noreferrer" className="no-underline">Yuqing Yang</a>
      </Typography>,
      affiliation: 'Work done during the Microsoft Research Internship',
      conference: 'ICLR 2026',
      year: 2026,
      paperLink: "https://arxiv.org/pdf/2602.23008",
      projectLink: "https://agent-lightning.github.io/posts/empo2/",
      posterLink: "https://docs.google.com/presentation/d/1z4lMIDvenCjwWz90ma0Tib2dmU8RbtO4Jo7XPSafzyw/edit?usp=sharing",
      codeLink: "https://github.com/microsoft/agent-lightning/tree/main/contrib/recipes/envs",
      tags: ['LLM Reasoning', 'Reinforcement Learning'],
    },
    {
      selected: false,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [C8] RL-Studio: A System for Multi-Phase Reinforcement Learning Experimentation
      </Typography>,
      affiliation: 'Work done during the LG AI Internship',
      authors:
      <Typography>
        Whiyoung Jung, <a href="https://sunghoonhong.github.io/" target='_blank' rel="noreferrer" className="no-underline">Sunghoon Hong</a>,{" "}
        Deunsol Yoon, <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>,{" "}
        <a href="https://ctid282.github.io/" target='_blank' rel="noreferrer" className="no-underline">Yongjae Shin</a>,{" "}
        Suhyun Jung, Hyundam Yoo, <br/>Youngjin Kim, Chanwoo Moon,{" "}
        <a href="https://sites.google.com/view/woohyunglim" target='_blank' rel="noreferrer" className="no-underline">Woohyung Lim</a>,{" "}
        Soonyoung Lee, Kanghoon Lee
      </Typography>,
      conference: 'AAAI 2026 Demo',
      tags: ['Reinforcement Learning'],
      year: 2026,
      paperLink: "https://ojs.aaai.org/index.php/AAAI/article/view/42358",
    },
    {
      selected: false,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [W1] RAISE: Enhancing Scientific Reasoning in LLMs via Step-by-Step Retrieval
      </Typography>,
      authors:
      <Typography>
        Minhae Oh,{" "}
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>,{" "}
        Nakyung Lee,{" "}
        Donggeon Seo,{" "}
        <a href="https://sites.google.com/view/hyu-nlp" target='_blank' rel="noreferrer" className="no-underline">Taeuk Kim</a>,{" "}
        <a href="https://cml.snu.ac.kr/?page_id=5948" target='_blank' rel="noreferrer" className="no-underline">Jungwoo Lee</a>
      </Typography>,
      year: 2025,
      paperLink: "https://arxiv.org/pdf/2506.08625",
      conference: 'NeurIPS Workshop on Mathematical Reasoning and AI 2025',
      tags: ['LLM Reasoning'],
    },
    {
      selected: true,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [C7] ReflAct: World-Grounded Decision Making in LLM Agents via Goal-State Reflection
      </Typography>,
      authors:
      <Typography>
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>*,{" "}
        Sojeong Rhee*,{" "}
        <a href="https://minbeomkim.github.io/" target='_blank' rel="noreferrer" className="no-underline">Minbeom Kim</a>,{" "}
        <a href="https://dohyungk1m.github.io/" target='_blank' rel="noreferrer" className="no-underline">Dohyung Kim</a>,{" "}
        Sangmook Lee,{" "}
        <a href="https://sites.google.com/view/youngchulsung" target='_blank' rel="noreferrer" className="no-underline">Youngchul Sung</a>†,{" "}
        <a href="http://milab.snu.ac.kr/kjung/index.html" target='_blank' rel="noreferrer" className="no-underline">Kyomin Jung</a>†
      </Typography>,
      year: 2025,
      paperLink: "https://www.arxiv.org/pdf/2505.15182",
      posterLink: "https://drive.google.com/file/d/16zqYnoTgnjmr02OmHEbB36rmMSOsehPl/view?usp=sharing",
      conference: 'EMNLP 2025',
      tags: ['LLM Reasoning'],
    },
    {
      selected: true,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [C6] Penalizing Infeasible Actions and Reward Scaling in Reinforcement Learning with Offline Data
      </Typography>,
      authors:
      <Typography>
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>,{" "}
        <a href="https://ctid282.github.io/" target='_blank' rel="noreferrer" className="no-underline">Yongjae Shin</a>,{" "}
        Whiyoung Jung,{" "}
        <a href="https://sunghoonhong.github.io/" target='_blank' rel="noreferrer" className="no-underline">Sunghoon Hong</a>,{" "}
        Deunsol Yoon,{" "}
        <a href="https://sites.google.com/view/youngchulsung" target='_blank' rel="noreferrer" className="no-underline">Youngchul Sung</a>,{" "}
        Kanghoon Lee,{" "}
        <a href="https://sites.google.com/view/woohyunglim" target='_blank' rel="noreferrer" className="no-underline">Woohyung Lim</a>
      </Typography>,
      year: 2025,
      paperLink: "https://arxiv.org/pdf/2507.08761",
      codeLink: "https://github.com/LGAI-Research/pars",
      posterLink: "https://drive.google.com/file/d/1TO-X7eDPFYi3knDGBOkPzR8i7jHKCjTW/view?usp=sharing",
      conference: 'ICML 2025',
      affiliation: 'Work done during the LG AI Internship',
      additionalInfo: <Typography sx={{ fontSize: '1rem', color: 'red', marginLeft: '8px', whiteSpace: 'nowrap' }}><b>Spotlight (Top 2.6%)</b></Typography>,
      tags: ['Reinforcement Learning'],
    },
    {
      selected: false,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [C5] Online Pre-Training for Offline-to-Online Reinforcement Learning
      </Typography>,
      authors:
      <Typography>
        <a href="https://ctid282.github.io/" target='_blank' rel="noreferrer" className="no-underline">Yongjae Shin</a>,{" "}
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>,{" "}
        Whiyoung Jung,{" "}
        <a href="https://sunghoonhong.github.io/" target='_blank' rel="noreferrer" className="no-underline">Sunghoon Hong</a>,{" "}
        Deunsol Yoon,{" "}
        <a href="https://www.ysjang.me/" target='_blank' rel="noreferrer" className="no-underline">Youngsoo Jang</a>,{" "}
        <a href="https://sites.google.com/view/ghkim/" target='_blank' rel="noreferrer" className="no-underline">Geon-Hyeong Kim</a>,{" "}
        Jongseong Chae,{" "}
        <a href="https://sites.google.com/view/youngchulsung" target='_blank' rel="noreferrer" className="no-underline">Youngchul Sung</a>,{" "}
        Kanghoon Lee,{" "}
        <a href="https://sites.google.com/view/woohyunglim" target='_blank' rel="noreferrer" className="no-underline">Woohyung Lim</a>
      </Typography>,
      year: 2025,
      paperLink: "https://openreview.net/pdf?id=5anr1H5vR5",
      codeLink: "https://github.com/LGAI-Research/opt",
      posterLink: "https://drive.google.com/file/d/11cd_PQusYnhRLbFJ7lPGs5a9Y0aL48nE/view?usp=sharing",
      affiliation: 'Work done during the LG AI Internship',
      conference: 'ICML 2025',
      tags: ['Reinforcement Learning'],
    },
    {
      selected: false,
      title: <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
        [C4] ARS: Adaptive Reward Scaling for Multi-Task Reinforcement Learning
      </Typography>,
      authors:
      <Typography>
        Myungsik Cho,{" "}
        Jongeui Park,{" "}
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>,{" "}
        <a href="https://sites.google.com/view/youngchulsung" target='_blank' rel="noreferrer" className="no-underline">Youngchul Sung</a>
      </Typography>,
      year: 2025,
      paperLink: "https://openreview.net/pdf?id=U0mI6M7lvI",
      posterLink: "https://drive.google.com/file/d/1x1iuhAeWiG8CEX9TjEnfyiKgR6xWLHqO/view?usp=sharing",
      conference: 'ICML 2025',
      tags: ['Reinforcement Learning'],
    },
    {
      selected: true,
      title: (
        <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
          [C3] Adaptive Q-Aid for Conditional Supervised Learning in Offline Reinforcement Learning
        </Typography>
      ),
      authors:
      <Typography>
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>,{" "}
        <a href="https://suyoung-lee.github.io/" target='_blank' rel="noreferrer" className="no-underline">Suyoung Lee</a>,{" "}
        <a href="https://sites.google.com/view/wjkim1202/" target='_blank' rel="noreferrer" className="no-underline">Woojun Kim</a>,{" "}
        <a href="https://sites.google.com/view/youngchulsung" target='_blank' rel="noreferrer" className="no-underline">Youngchul Sung</a>
      </Typography>,
      conference: 'NeurIPS 2024',
      year: 2024,
      paperLink: "https://arxiv.org/pdf/2402.02017",
      codeLink: "https://github.com/beanie00/QCS",
      posterLink: "https://drive.google.com/file/d/11pabw7Knmn9eAdWdCwmz_izzrjUCms9q/view?usp=sharing",
      tags: ['Reinforcement Learning'],
    },
    {
      selected: true,
      title: (
        <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
          [C2] Decision ConvFormer: Local Filtering in MetaFormer is Sufficient for Decision Making
        </Typography>
      ),
      authors:
      <Typography>
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>,{" "}
        <a href="https://suyoung-lee.github.io/" target='_blank' rel="noreferrer" className="no-underline">Suyoung Lee</a>,{" "}
        <a href="https://sites.google.com/view/wjkim1202/" target='_blank' rel="noreferrer" className="no-underline">Woojun Kim</a>†,{" "}
        <a href="https://sites.google.com/view/youngchulsung" target='_blank' rel="noreferrer" className="no-underline">Youngchul Sung</a>†
      </Typography>,
      conference: 'ICLR 2024',
      additionalInfo: <>
        <Typography sx={{ fontSize: '1rem', color: 'red', marginLeft: '8px', whiteSpace: 'nowrap' }}><b>Spotlight (Top 5%)</b></Typography>
        <Typography sx={{ fontSize: '1rem', width: '100%' }}>NeurIPS Workshop on Foundation Models for Decision Making 2023</Typography>
      </>,
      year: 2024,
      paperLink: "https://arxiv.org/pdf/2310.03022",
      codeLink: "https://github.com/beanie00/decision-convformer",
      posterLink: "https://drive.google.com/file/d/1IMS9LD_UJoCvOoQWiFPJKa-E7HTN3nSp/view?usp=sharing",
      tags: ['Reinforcement Learning'],
    },
    {
      selected: true,
      title: (
        <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
          [C1] LESSON: Learning to Integrate Exploration Strategies for Reinforcement Learning via an Option Framework
        </Typography>
      ),
      authors:
      <Typography>
        <a href="https://sites.google.com/view/wjkim1202/" target='_blank' rel="noreferrer" className="no-underline">Woojun Kim</a>*,{" "}
        <strong><a href="https://www.beanie00.com/" target='_blank' rel="noreferrer" className="no-underline">Jeonghye Kim</a></strong>*,{" "}
        <a href="https://sites.google.com/view/youngchulsung" target='_blank' rel="noreferrer" className="no-underline">Youngchul Sung</a>
      </Typography>,
      conference: 'ICML 2023',
      year: 2023,
      paperLink: "https://arxiv.org/pdf/2310.03342",
      codeLink: "https://github.com/beanie00/LESSON",
      posterLink: "https://drive.google.com/file/d/1sTN0yR68_lteY0pQ4CplW5Kx_HHcDgJH/view?usp=sharing",
      tags: ['Reinforcement Learning'],
    }
  ];

  return (
    <Box>
      <Typography sx={{ mb: 1 }}>
        <b>C</b>: Conference / <b>P</b>: Preprint / <b>W</b>: Workshop /{' '}
        <StarIcon size={17} style={{ verticalAlign: '-3px' }} /> <b>Selected</b> (First / Co-first Author)
      </Typography>
      <Typography sx={{mb: 6}}> (*: equal contribution, †: equal corresponding)</Typography>
      {publications.map((pub, index) => (
        <React.Fragment key={index}>
          {(index === 0 || publications[index - 1].year !== pub.year) && (
            <YearHeading year={pub.year} isFirst={index === 0} />
          )}
          <PublicationItem isMobile={isMobile} {...pub} />
        </React.Fragment>
      ))}
    </Box>
  );
}

Publications.propTypes = {
  isMobile: PropTypes.bool.isRequired
};
